import React from "react";
import { toast } from "react-toastify";
import { usePublishShiftsMutation } from "store/reducers/organisation";

type PercentProgress = {
  state: "PROGRESS";
  message: string;
  progress?: number;
  total?: number;
};

type DoneProgress = {
  state: "DONE";
  message: string;
};

type WarningProgress = {
  state: "WARNING";
  message: string;
};

type ErrorProgress = {
  state: "ERROR";
  message: string;
  error: { [key: string]: any };
};

export type ProgressMessage = DoneProgress | PercentProgress | WarningProgress | ErrorProgress;

interface UsePublishShiftsWithProgressOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export function usePublishShiftsWithProgress(options: UsePublishShiftsWithProgressOptions = {}) {
  const [publishShifts, { isLoading: isPublishing, error: publishError }] = usePublishShiftsMutation();
  const [hasPublishedShifts, setHasPublishedShifts] = React.useState<"success" | "error">();
  const [progressList, setProgressList] = React.useState<ProgressMessage[]>();
  const [ws, setWebSocket] = React.useState<WebSocket>();

  const WEBSOCKET_URL =
    import.meta.env.VITE_WEBSOCKET_URL || location.origin.replace("https", "wss").replace("http", "ws") + "/socket";

  React.useEffect(() => {
    // websocket cleanup
    return function () {
      if (ws) {
        ws.close();
      }
    };
  }, [ws]);

  function connectWs(task_id: string) {
    let websocket = new WebSocket(WEBSOCKET_URL);
    setWebSocket(websocket);
    websocket.onerror = () => {
      toast("WebSocket error", {
        type: "error",
      });
    };
    websocket.addEventListener("open", async function (_event) {
      setProgressList([]);
      websocket.send(JSON.stringify({ task_id }));
    });

    websocket.addEventListener("message", function (event) {
      if (event.data) {
        let data = JSON.parse(event.data);
        setProgressList((state) => (state ? [...state, data] : [data]));

        if (data.state === "DONE") {
          setHasPublishedShifts("success");
          websocket.close();
          options.onSuccess?.();
        }
        if (data.state === "ERROR") {
          setHasPublishedShifts("error");
          websocket.close();
          options.onError?.(data.error);
        }
      }
    });
  }

  const executePublishShifts = async (params: {
    organisation: Organisation;
    startDate: Date;
    endDate: Date;
    employee_ids?: number[];
  }) => {
    setHasPublishedShifts(undefined);
    setProgressList(undefined);

    try {
      const response = await publishShifts(params).unwrap();
      const task_id = response;
      connectWs(task_id);
    } catch (error) {
      const err = error as { status: number; data?: any };
      if (err.status === 500) {
        const json = err.data;
        if (json.Type) {
          toast(`Xero ${json.Type}: ${json.Message}`, { type: "error" });
        } else if (json.Title) {
          toast(`${json.Title}: ${json.Detail}`, { type: "error" });
        }
      }
      options.onError?.(error);
    }
  };

  return {
    executePublishShifts,
    isPublishing,
    publishError,
    hasPublishedShifts,
    progressList,
  };
}
